import { List, type ListImperativeAPI } from "react-window";
import { Card, PostMiniCardBase } from "./post-mini-card";
import { useEffect, useRef } from "react";
import type { CSSProperties } from "react";

// Card height calculation: PostMiniCardBase height (55px) + gap-[8px] spacing
// WARNING: If PostMiniCardBase styling changes, this constant must be updated
const CARD_HEIGHT = 63;

// Container height based on typical modal viewport
// TODO: Consider making this responsive based on viewport height or measuring container dynamically
const DEFAULT_CONTAINER_HEIGHT = 600;

// Props passed to row component via rowProps (excludes index, style, ariaAttributes which are injected by List)
interface RowProps {
  cards: Card[];
  onSelect: (card: Card) => void;
  onClose: (card: Card) => void;
  onDuplicate?: (card: Card) => void;
  isReachedMaxFormCount: boolean;
  isClustered: boolean;
}

// Row component receives ariaAttributes, index, style (injected by List) plus RowProps
const MiniCardRow = ({
  index,
  style,
  cards,
  onSelect,
  onClose,
  onDuplicate,
  isReachedMaxFormCount,
  isClustered,
}: {
  ariaAttributes: {
    "aria-posinset": number;
    "aria-setsize": number;
    role: "listitem";
  };
  index: number;
  style: CSSProperties;
} & RowProps) => {
  const card = cards[index];
  return (
    <div style={style} className="px-2">
      <PostMiniCardBase
        card={card}
        onClick={() => onSelect(card)}
        onClose={() => onClose(card)}
        onDuplicate={() => onDuplicate?.(card)}
        showDuplicate={!isReachedMaxFormCount}
        isClustered={isClustered}
      />
    </div>
  );
};

interface VirtualizedMiniCardListProps {
  cards: Card[];
  onSelect: (card: Card) => void;
  onClose: (card: Card) => void;
  onDuplicate?: (card: Card) => void;
  isReachedMaxFormCount: boolean;
  selectedCardIndex: number;
  isClustered: boolean;
  containerHeight?: number; // Optional: override default container height
}

export const VirtualizedMiniCardList = ({
  cards,
  onSelect,
  onClose,
  onDuplicate,
  isReachedMaxFormCount,
  selectedCardIndex,
  isClustered,
  containerHeight = DEFAULT_CONTAINER_HEIGHT,
}: VirtualizedMiniCardListProps) => {
  const listRef = useRef<ListImperativeAPI>(null);

  // Scroll to selected card when it changes
  useEffect(() => {
    if (selectedCardIndex >= 0 && listRef.current) {
      listRef.current.scrollToRow({ index: selectedCardIndex, align: "smart" });
    }
  }, [selectedCardIndex]);

  return (
    <List<RowProps>
      listRef={listRef}
      rowComponent={MiniCardRow}
      rowCount={cards.length}
      rowHeight={CARD_HEIGHT}
      rowProps={{
        cards,
        onSelect,
        onClose,
        onDuplicate,
        isReachedMaxFormCount,
        isClustered,
      }}
      className="scrollbar-thin"
      style={{ height: containerHeight }}
    />
  );
};
